import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import * as d3 from 'd3'
import { useGetSize } from 'Hooks/useGetSize'
import { AnimatedNote } from 'components/ExampleComponent/AnimatedNote'
import { GridLine } from 'theme/styles'

const RatioChart = ({ year }: { year: number }) => {
  const [data, setData] = useState<RatioData[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const isMobile = window.innerWidth < 768
  const { width } = useGetSize(containerRef)
  const height = isMobile ? width : width * 0.75

  const minX = d3.min(data, (d) => d.year) ?? 0
  const maxX = d3.max(data, (d) => d.year) ?? 0
  const maxY = d3.max(data, (d) => d.ratio) ?? 0

  const xDomain = [minX, maxX]
  const yDomain = [0, maxY + maxY * 0.1]
  const margin = {
    top: 30,
    right: 0,
    bottom: 30,
    left: 50,
  }
  const tickYLength = 18
  const tickXLength = 12

  const xScale = d3
    .scaleLinear()
    .domain(xDomain)
    .range([margin.left, width - margin.right])

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([height - margin.bottom, margin.top])

  const lineGenerator = d3.line() //.curve(d3.curveCatmullRom.alpha(0.5))

  const ageDataLine: [number, number][] = data.map((d) => {
    return [xScale(d.year), yScale(d.ratio)]
  })

  const ageDataArea = [...ageDataLine]

  ageDataArea.unshift([xScale(minX), height - margin.bottom])
  ageDataArea.push([width - margin.right, height - margin.bottom])

  const ageLinePath = lineGenerator(ageDataLine) || undefined
  const ageAreaPath = lineGenerator(ageDataArea) || undefined

  const xTicks = data
    .filter((d) => d.year % (isMobile ? 10 : 5) === 0 || d.year === minX)
    .map((d) => d.year)
  const yTicks = [0, 1, 2, 3, 4]

  useEffect(() => {
    // Retired PCT data
    d3.csv<RatioData>(
      `${
        process.env.NODE_ENV === 'production'
          ? 'https://dev-interaktiv-mf.tagesanzeiger.ch/2024/votations-federales-avs'
          : ''
      }/ratio_data.csv`,
      (d) => {
        return {
          year: +d['year'],
          ratio: +d['ratio'],
        }
      }
    )
      .then((data) => {
        setData(data)
      })
      .catch((error) => {
        console.error('Error fetching the CSV data:', error)
      })
  }, [])

  return (
    <Container ref={containerRef}>
      <Svg ref={svgRef} width={width} height={height}>
        {/* Y grid */}
        {/* {xTicks.map((value) => (
            <GridLine
              x1={xScale(value)}
              x2={xScale(value)}
              y1={margin.top}
              y2={height - margin.bottom}
            />
          ))} */}

        {/* X ticks */}
        {xTicks.map((value) => (
          <Tick
            x1={xScale(value)}
            x2={xScale(value)}
            y1={height - margin.bottom + tickXLength}
            y2={height - margin.bottom}
          />
        ))}

        {/* X grid */}
        {yTicks.map((value) => {
          return (
            <GridLine
              x1={0}
              x2={width - margin.right}
              y1={yScale(value)}
              y2={yScale(value)}
            />
          )
        })}

        {/* Y ticks */}
        {/* {yTicks.map((value) => {
            return (
              <Tick
                x1={margin.left - tickYLength - 20}
                x2={margin.left - 20}
                y1={yScale(value)}
                y2={yScale(value)}
              />
            )
          })} */}

        <Area d={ageAreaPath} />

        <BottomLine
          x1={0}
          x2={width - margin.right}
          y1={height - margin.bottom}
          y2={height - margin.bottom}
        />
      </Svg>

      {/* Y labels */}
      {yTicks.map((value) => (
        <Label
          style={{
            top: yScale(value) - 28,
            left: -30,
            transform: 'translateX(0)',
            textAlign: 'right',
            width: 50,
          }}
        >
          {value.toLocaleString('fr-CH')}
        </Label>
      ))}

      <Label
        style={{
          top: 0,
          left: 0,
          transform: 'translateX(0)',
          textAlign: 'left',
          letterSpacing: 0.85,
          width: 150,
        }}
      >
        <strong>Rapport entre population active et retraités</strong>
      </Label>

      {/* X labels */}
      {xTicks.map((value) => (
        <Label style={{ left: xScale(value), top: height }}>{value}</Label>
      ))}

      <AnimatedNote
        direction='left'
        show={true}
        position={[yScale(4), xScale(2000)]}
      >
        Il y avait plus de <strong>quatre actifs</strong> pour un retraité dans les années 90
      </AnimatedNote>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const Line = styled.path`
  stroke-width: 3px;
  stroke: #ffe462;
  fill: none;
`

const Area = styled.path`
  stroke: none;
  fill: ${({ theme }) => `rgb(${theme.mainColor})`};
`

const Tick = styled.line`
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const BottomLine = styled.line`
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const Label = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  color: var(--text-color);
  transform: translate(-50%, -50%);
  font-size: 0.8em;
  line-height: 1.1;
`

const Svg = styled.svg``

export default RatioChart
